import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useAuthContext } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { Mail, Loader2, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

export function ConfirmEmailPage() {
  const location = useLocation()
  const { t } = useTranslation()
  const { resendConfirmationEmail } = useAuthContext()

  const email = (location.state as { email?: string })?.email || ''
  const [isResending, setIsResending] = useState(false)

  const handleResend = async () => {
    if (!email) {
      toast.error(t('auth.toastNoEmailProvided'))
      return
    }

    setIsResending(true)
    const { error } = await resendConfirmationEmail(email)
    setIsResending(false)

    if (error) {
      toast.error(error.message || t('auth.toastResendFailed'))
      return
    }

    toast.success(t('auth.toastConfirmationSent'))
  }

  return (
    <div className="min-h-screen bg-warm-gray flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
            <Mail className="w-10 h-10 text-accent" />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-gray-900">{t('auth.checkYourEmail')}</h1>
            <p className="text-gray-500">
              {email ? (
                <Trans
                  i18nKey="auth.confirmationSentTo"
                  values={{ email }}
                  components={{ strong: <span className="font-medium text-gray-900" /> }}
                />
              ) : (
                t('auth.confirmationSentToFallback')
              )}
            </p>
          </div>

          <div className="p-4 rounded-xl bg-white border border-border space-y-4">
            <div className="space-y-2 text-left">
              <h3 className="font-medium text-gray-900">{t('auth.nextStepsHeading')}</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-medium flex-shrink-0">
                    1
                  </span>
                  <span>{t('auth.confirmStep1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-medium flex-shrink-0">
                    2
                  </span>
                  <span>{t('auth.confirmStep2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-medium flex-shrink-0">
                    3
                  </span>
                  <span>{t('auth.confirmStep3')}</span>
                </li>
              </ol>
            </div>
          </div>

          <div className="space-y-3">
            {email && (
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={isResending}
                className="gap-2"
              >
                {isResending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {t('auth.resendConfirmation')}
              </Button>
            )}

            <p className="text-sm text-gray-500">
              {t('auth.alreadyConfirmed')}{' '}
              <Link to="/login" className="font-medium text-accent hover:underline">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>

          <p className="text-xs text-gray-400">{t('auth.confirmationExpiry')}</p>
        </div>
      </div>
    </div>
  )
}
